import type { ReactNode } from 'react';
import React from 'react';
import { isEmpty } from 'lodash';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { themes } from './BlockLink.themes';
export type BlockLinkProps = {
  className?: string;
  dataTestId?: string;
  icon: IconName;
  onClick?: () => void;
  secondaryTexts?: Array<string>;
  target?: string;
  texts: ReadonlyArray<string | Node>;
  textsDataTestIds?: Array<string>;
  theme?: keyof typeof themes;
  url: string;
};
type TextsProps = {
  className?: string;
  texts: ReadonlyArray<string>;
  textsDataTestIds?: ReadonlyArray<string>;
  theme: keyof typeof themes;
};
const DEFAULT_DATA_TEST_ID = 'BlockLink-text';

const BlockLinkTexts = ({
  className,
  texts = [],
  textsDataTestIds = [],
  theme,
}: TextsProps): Array<Node> => {
  return texts
    .filter((text) => !isEmpty(text))
    .map((text, idx) => {
      const dataTestId =
        idx < textsDataTestIds.length ? textsDataTestIds[idx] : DEFAULT_DATA_TEST_ID;
      return (
        <span
          className={classnames('block', themes[theme][`thCustomText${idx}`], className)}
          data-testid={dataTestId}
          key={idx}
        >
          {text}
        </span>
      );
    });
};

export const BlockLink = ({
  className,
  dataTestId,
  icon,
  onClick = () => {},
  secondaryTexts = [],
  target = '_blank',
  texts = [],
  textsDataTestIds = [],
  theme = 'default',
  url = '',
}: BlockLinkProps) => {
  const title = (texts || []).filter((t) => typeof t === 'string').join(' ');
  const linkTarget = url.startsWith('tel:') ? '' : target;
  const { thIconWidth, thRoot, thTexts, thSText } = themes[theme];
  return !!texts.length || icon ? (
    <a
      className={classnames(thRoot, className)}
      data-testid={dataTestId}
      href={url}
      onClick={onClick}
      rel="noopener noreferrer"
      target={linkTarget}
      title={title}
    >
      <Icon className="shrink-0" name={icon} width={thIconWidth} />
      {!!texts.length && (
        <span className={thTexts}>
          <BlockLinkTexts texts={texts} textsDataTestIds={textsDataTestIds} theme={theme} />
          <BlockLinkTexts className={thSText} texts={secondaryTexts} theme={theme} />
        </span>
      )}
    </a>
  ) : null;
};
